import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
/* Dopo index.css di proposito: gli override del tema chiaro devono venire
   dopo le utility di Tailwind per vincere a parita' di specificita'.
   Generato da scripts/gen-theme-overrides.js. */
import "@/theme-light.generated.css";
import { Analytics } from "@vercel/analytics/react";
import App from "@/App";

// Service Worker control: allow disabling in development or via env
const DISABLE_SW =
	process.env.REACT_APP_DISABLE_SW === "true" ||
	window.location.hostname.includes("localhost") ||
	window.location.hostname === "127.0.0.1";
const SW_VERSION = process.env.REACT_APP_SW_VERSION || "2026-06-12";
const APP_VERSION_STORAGE_KEY = "kraveon_app_version";
const VERSION_RELOAD_STORAGE_KEY = "kraveon_version_reload_done";

function buildVersionedServiceWorkerUrl() {
	return `/service-worker.js?v=${encodeURIComponent(SW_VERSION)}`;
}

function isVersionedScriptUrl(scriptUrl) {
	if (!scriptUrl) return false;
	return scriptUrl.includes(`v=${encodeURIComponent(SW_VERSION)}`);
}

async function purgeCachesOnVersionChange() {
	if (!("caches" in window)) {
		return false;
	}

	const previousVersion = window.localStorage.getItem(APP_VERSION_STORAGE_KEY);
	if (!previousVersion) {
		window.localStorage.setItem(APP_VERSION_STORAGE_KEY, SW_VERSION);
		return false;
	}

	if (previousVersion === SW_VERSION) {
		window.sessionStorage.removeItem(VERSION_RELOAD_STORAGE_KEY);
		return false;
	}

	window.localStorage.setItem(APP_VERSION_STORAGE_KEY, SW_VERSION);

	try {
		const cacheKeys = await caches.keys();
		await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
	} catch (_) {}

	return true;
}

function wireServiceWorkerAutoUpdate(registration) {
	if (!registration) return;

	let hasRefreshedForNewSw = false;

	const forceActivateWaitingWorker = () => {
		if (registration.waiting) {
			registration.waiting.postMessage({ type: "SKIP_WAITING" });
		}
	};

	navigator.serviceWorker.addEventListener("controllerchange", () => {
		if (hasRefreshedForNewSw) return;
		hasRefreshedForNewSw = true;
		window.location.reload();
	});

	registration.addEventListener("updatefound", () => {
		const installingWorker = registration.installing;
		if (!installingWorker) return;

		installingWorker.addEventListener("statechange", () => {
			if (
				installingWorker.state === "installed" &&
				navigator.serviceWorker.controller
			) {
				forceActivateWaitingWorker();
			}
		});
	});

	forceActivateWaitingWorker();
	registration.update().catch(() => {});

	const checkForUpdates = () => {
		registration.update().catch(() => {});
	};

	window.addEventListener("focus", checkForUpdates);
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") {
			checkForUpdates();
		}
	});

	window.setInterval(checkForUpdates, 30 * 60 * 1000);
}

async function ensureLatestServiceWorkerRegistration() {
	const versionedSwUrl = buildVersionedServiceWorkerUrl();
	const existingRegistration =
		await navigator.serviceWorker.getRegistration("/");

	if (existingRegistration?.active?.scriptURL) {
		const isAlreadyVersioned = isVersionedScriptUrl(
			existingRegistration.active.scriptURL,
		);
		if (!isAlreadyVersioned) {
			await existingRegistration.unregister();
		}
	}

	return navigator.serviceWorker.register(versionedSwUrl, {
		updateViaCache: "none",
	});
}

if ("serviceWorker" in navigator) {
	// If disabled, unregister any existing service workers (useful after deploy)
	if (DISABLE_SW) {
		navigator.serviceWorker
			.getRegistrations()
			.then((regs) => regs.forEach((r) => r.unregister()))
			.then(() => console.log("Service Worker unregistered (dev mode)"))
			.catch(() => {});
	} else {
		// Normal registration (production)
		window.addEventListener("load", () => {
			purgeCachesOnVersionChange()
				.then((didPurgeCaches) => {
					return ensureLatestServiceWorkerRegistration().then(
						(registration) => {
							console.log("SW registered: ", registration);
							wireServiceWorkerAutoUpdate(registration);

							if (
								didPurgeCaches &&
								window.sessionStorage.getItem(VERSION_RELOAD_STORAGE_KEY) !==
									SW_VERSION
							) {
								window.sessionStorage.setItem(
									VERSION_RELOAD_STORAGE_KEY,
									SW_VERSION,
								);
								window.location.reload();
							}
						},
					);
				})
				.catch((error) => {
					console.log("SW registration failed: ", error);
				});
		});
	}
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
	<React.StrictMode>
		<App />
		<Analytics />
	</React.StrictMode>,
);
